import React, { useCallback } from 'react';
import {
  EditorProvider,
  Editor,
  Toolbar,
  BtnBold,
  BtnItalic,
  BtnUnderline,
  Separator,
  createDropdown,
} from 'react-simple-wysiwyg';
import { Box } from '@mui/material';

const BtnFontSize = createDropdown('Font Size', [
  ['Small', 'fontSize', '2'],
  ['Normal', 'fontSize', '3'],
  ['Large', 'fontSize', '5'],
  ['X-Large', 'fontSize', '6'],
]);

const COLORS = [
  { label: 'Black', value: '#000000' },
  { label: 'Red', value: '#c62828' },
  { label: 'Blue', value: '#1565c0' },
  { label: 'Green', value: '#2e7d32' },
  { label: 'Orange', value: '#e65100' },
  { label: 'Purple', value: '#6a1b9a' },
  { label: 'Gray', value: '#616161' },
];

const ColorButton = () => {
  const handleColor = useCallback((color) => {
    document.execCommand('foreColor', false, color);
  }, []);

  return (
    <span className="rsw-btn" style={{ position: 'relative', padding: 0 }}>
      <select
        title="Font Color"
        onChange={(e) => { handleColor(e.target.value); e.target.value = ''; }}
        style={{
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          fontSize: 14,
          padding: '2px 4px',
          width: 28,
          height: 28,
          color: 'transparent',
        }}
        defaultValue=""
      >
        <option value="" disabled>A</option>
        {COLORS.map((c) => (
          <option key={c.value} value={c.value} style={{ color: c.value }}>
            {c.label}
          </option>
        ))}
      </select>
      <span
        style={{
          position: 'absolute', left: '50%', top: '50%',
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'none', fontWeight: 700, fontSize: 14,
          background: 'linear-gradient(180deg, #c62828 0%, #1565c0 50%, #2e7d32 100%)',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
        }}
      >
        A
      </span>
    </span>
  );
};

const toolbarButtons = (
  <>
    <BtnBold />
    <BtnItalic />
    <BtnUnderline />
    <Separator />
    <BtnFontSize />
    <Separator />
    <ColorButton />
  </>
);

const commonSx = {
  '& .rsw-editor': {
    fontFamily: 'inherit',
    fontSize: 14,
    lineHeight: 1.6,
  },
  '& .rsw-ce': {
    padding: '8px 12px',
    outline: 'none',
  },
  '& .rsw-toolbar': {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '2px',
    padding: '4px',
    backgroundColor: '#fafafa',
  },
  '& .rsw-btn': {
    minWidth: 28,
    height: 28,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    background: 'none',
    borderRadius: 4,
    cursor: 'pointer',
    '&:hover': { backgroundColor: '#e0e0e0' },
    '&[data-active="true"]': { backgroundColor: '#d0d0d0' },
  },
  '& .rsw-separator': {
    width: 1,
    height: 20,
    margin: '4px 4px',
    backgroundColor: '#ddd',
  },
  '& .rsw-dd': {
    fontSize: 12,
  },
};

const SimpleEditor = ({ value, onChange, onKeyUp, onBlur, minHeight = 120, placeholder = '', toolbarOutside = false, renderHeader }) => {
  const editorProps = {
    value: value || '',
    onChange,
    onKeyUp,
    onBlur,
    containerProps: { style: { border: '1px solid #ccc', borderRadius: 4, minHeight } },
  };

  if (renderHeader) {
    const toolbarJsx = <Toolbar>{toolbarButtons}</Toolbar>;
    return (
      <Box sx={commonSx}>
        <EditorProvider>
          {renderHeader(toolbarJsx)}
          <Editor {...editorProps} />
        </EditorProvider>
      </Box>
    );
  }

  if (toolbarOutside) {
    return (
      <Box sx={commonSx}>
        <EditorProvider>
          <Toolbar>{toolbarButtons}</Toolbar>
          <Editor {...editorProps} />
        </EditorProvider>
      </Box>
    );
  }

  return (
    <Box sx={{ ...commonSx, '& .rsw-toolbar': { ...commonSx['& .rsw-toolbar'], borderBottom: '1px solid #e0e0e0' } }}>
      <EditorProvider>
        <Editor {...editorProps}>
          <Toolbar>{toolbarButtons}</Toolbar>
        </Editor>
      </EditorProvider>
    </Box>
  );
};

export default SimpleEditor;
